import { Injectable, inject, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { AppSettingsService } from "@shared/app-settings-service";
import { BroadcastService } from "@shared/broadcast-service";
import { defer, filter, firstValueFrom, map, Observable, Subject, Subscription, take, timeout } from "rxjs";

export class IframeMessage {
    origin: string;
    data: any;
    constructor(origin: string, data: any) {
        this.origin = origin;
        this.data = data;
    }
}

@Injectable({
    providedIn: "root",
})
export class IframeMessagesService {
    private readonly appSettingsService = inject(AppSettingsService);

    private allowedSenders: string[] = [];
    public initDone = false;
    public messagesOrigin = signal<string | undefined>(undefined);
    public messageOriginObservable = toObservable(this.messagesOrigin);
    public InitDoneSubject = new Subject<any>();
    InitDoneEvent$ = this.InitDoneSubject.asObservable();

    constructor() {
        window.addEventListener("message", (event) => {
            if (event.data?.message !== undefined) {
                if (!this.messagesOrigin()) {
                    this.messagesOrigin.set(event.origin);
                }
                if (event.data.message === "initSessionInfo" || this.messageOriginValid(event.origin)) {
                    BroadcastService.broadcast(event.data.message, new IframeMessage(event.origin, event.data.data));
                }
            }
        });
    }

    init(): void {
        this.allowedSenders = this.appSettingsService
            .getSetting("AllowedSenders")
            .split(";")
            .filter((it) => it.trim().length > 0);
        this.InitDoneSubject.next(true);
        this.initDone = true;
    }

    public messageOriginValid(origin: string): boolean {
        return this.allowedSenders.some((it) => this.match(it, origin));
    }

    public postMessage(message: string, data: any) {
        this.messageOriginObservable
            .pipe(
                filter((value) => value !== undefined),
                take(1),
            )
            .subscribe((origin) => {
                const msg = {
                    message,
                    data,
                };
                parent.postMessage(msg, origin);
            });
    }

    public postMessageResp(message: string, data: any, responseMessage: string, messageResponseTimeout = 600000): Promise<any> {
        return firstValueFrom(this.postMessageRespObservable(message, responseMessage, data, messageResponseTimeout));
    }

    public postMessageRespObservable<TReq = void, TResp = unknown>(
        message: string,
        responseMessage: string,
        data?: TReq,
        messageResponseTimeout = 30000,
    ): Observable<TResp> {
        return defer(() => {
            const msgId = crypto.randomUUID();
            const outgoing = data || {};
            Object.assign(outgoing, { msgId });
            const ack$ = BroadcastService.on<IframeMessage>(message + "Ack").pipe(
                filter((msg) => msg.data?.msgId === msgId),
                take(1),
                timeout(5000),
            );
            const response$ = BroadcastService.on<IframeMessage>(responseMessage).pipe(
                filter((msg) => msg.data?.msgId === msgId),
                take(1),
                map((msg) => msg.data as TResp),
                timeout(messageResponseTimeout),
            );

            return new Observable<TResp>((subscriber) => {
                const subscriptions = new Subscription();
                let ackReceived = false;
                let responseReceived = false;
                let responseValue: TResp;

                const emitWhenComplete = () => {
                    if (ackReceived && responseReceived) {
                        subscriber.next(responseValue);
                        subscriber.complete();
                    }
                };

                // Listen for both correlated events before posting; the host can respond immediately.
                subscriptions.add(
                    ack$.subscribe({
                        next: () => {
                            ackReceived = true;
                            emitWhenComplete();
                        },
                        error: (error: unknown) => subscriber.error(error),
                    }),
                );
                subscriptions.add(
                    response$.subscribe({
                        next: (response) => {
                            responseValue = response;
                            responseReceived = true;
                            emitWhenComplete();
                        },
                        error: (error: unknown) => subscriber.error(error),
                    }),
                );

                try {
                    this.postMessage(message, outgoing);
                } catch (error) {
                    subscriber.error(error);
                }

                return subscriptions;
            });
        });
    }

    private match(first: string, second: string): boolean {
        if (first.length === 0 && second.length === 0) {
            return true;
        }
        if (first.length > 1 && first.startsWith("*") && second.length === 0) {
            return false;
        }

        if ((first.length > 1 && first.startsWith("?")) || (first.length !== 0 && second.length !== 0 && first[0] === second[0])) {
            return this.match(first.substring(1), second.substring(1));
        }

        if (first.length > 0 && first.startsWith("*")) {
            return this.match(first.substring(1), second) || this.match(first, second.substring(1));
        }
        return false;
    }
}
